import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CardMedia } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import type { AllStatus, AssetMetadata, VideoMetadata } from '~/services/VideoGenService';

interface MediaPlayBackCardProps {
  type: 'asset' | 'output';
  templateName: string;
  videoUrl: string;
  videoId: string;
  isPlaying: boolean;
  onPlay: () => void;
  onStop: () => void;
  metadata: AssetMetadata | VideoMetadata;
  status: AllStatus;
  mediaType: 'VIDEO' | 'AUDIO';
}

export default function MediaOverviewCard({
  videoId,
  videoUrl,
  isPlaying,
  onPlay,
  onStop,
  type,
  metadata,
  status,
  mediaType,
}: MediaPlayBackCardProps) {
  const isAudio = mediaType === 'AUDIO';
  const videoRef = useRef<HTMLVideoElement>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  let metaDataToShow = {};
  if (type === 'asset') {
    const assetMetadata = metadata as AssetMetadata;
    metaDataToShow = {
      id: assetMetadata.id,
      name: assetMetadata.name,
      offerId: assetMetadata.offerId,
      createdAt: assetMetadata.createdAt,
    };
  } else {
    const videoMetadata = metadata as VideoMetadata;
    metaDataToShow = {
      id: videoMetadata.id,
      templateName: videoMetadata.templateName,
      createdAt: videoMetadata.createdAt,
    };
  }

  useEffect(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    const ref = videoRef.current;
    return () => {
      ref?.pause();
    };
  }, []);

  const handlePlay = () => {
    onPlay();
  };

  const handlePause = () => {
    onStop();
  };

  const handleCopy = (value: unknown, key: string) => {
    const textToCopy = typeof value === 'object' ? JSON.stringify(value) : String(value);
    navigator.clipboard.writeText(textToCopy);
    setCopiedKey(key);
    enqueueSnackbar('Copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
      <Card sx={{ boxShadow: 3 }}>
        {status === 'ACTIVE' || status === 'SUCCEEDED' ? (
          <Box sx={{ position: 'relative' }}>
            <CardMedia
              component="video"
              src={`${videoUrl}#t=0.1`}
              controls
              preload="metadata"
              ref={videoRef}
              onPlay={handlePlay}
              onPause={handlePause}
              sx={{
                backgroundColor: isAudio ? 'grey.100' : 'grey.900',
                maxHeight: 500,
              }}
            />
            {isAudio && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  fontWeight: 'medium',
                }}
              >
                AUDIO
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: 'grey.100',
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary">
              {status}
            </Typography>
          </Box>
        )}
        <CardContent>
          {Object.entries(metaDataToShow).map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                {key}:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.primary' }}>
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </Typography>
                <IconButton size="small" onClick={() => handleCopy(value, key)} sx={{ p: 0.5 }}>
                  {copiedKey === key ? (
                    <CheckIcon fontSize="small" color="success" />
                  ) : (
                    <ContentCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              sx={{ textTransform: 'none' }}
              onClick={() =>
                window.open(
                  `/marketing/video-gen/preview/${type === 'asset' ? 'asset' : 'output-video'}/${videoId}`,
                  '_blank',
                )
              }
            >
              View Videos
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
